import React, { useState, useMemo } from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calender";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarIcon, Search, Users, ChevronUp, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";
import TransportCard from "./TransportCard";

/**
 * @param {{
 *   vehicles: Array<any>;
 *   onSelectVehicle: (vehicle: any) => void;
 *   prefillDate?: Date | null;
 *   prefillPassengers?: number;
 * }} props
 */
export default function TransportSearchView({ vehicles, onSelectVehicle, prefillDate, prefillPassengers }) {
  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const PopoverContentComp = /** @type {React.ComponentType<any>} */ (PopoverContent);

  const [selectedRoute, setSelectedRoute] = useState("__all__");
  const [selectedDate, setSelectedDate] = useState(prefillDate || null);
  const [passengers, setPassengers] = useState(prefillPassengers || 2);

  const uniqueDropoffs = useMemo(() => {
    const set = new Set(vehicles.map((v) => v.dropoff_location));
    return Array.from(set).sort();
  }, [vehicles]);

  const filteredVehicles = useMemo(() => {
    return vehicles.filter((v) => {
      if (selectedRoute === "__all__") return true;
      return v.dropoff_location === selectedRoute;
    });
  }, [vehicles, selectedRoute]);

  return (
    <div className="w-full bg-white">
      {/* Hero — masthead style */}
      <div className="relative">
        <div className="relative h-72 sm:h-80 bg-[#0B2421] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-20"
            style={{ backgroundImage: "url('https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=1200')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B2421]/40 to-[#0B2421]/80" />
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="relative h-full flex flex-col items-center justify-center text-center px-4 pb-16"
          >
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-2">Ground Transport</p>
            <h1 className="text-white font-bold text-3xl sm:text-4xl mb-1">Book Transportation</h1>
            <p className="text-white/60 text-sm">Ground transport in Batam</p>
          </motion.div>
        </div>

        {/* Search form overlapping the banner */}
        <div className="absolute bottom-0 left-0 right-0 z-30 translate-y-1/2 px-3 sm:px-4">
          <div className="max-w-4xl mx-auto rounded-2xl shadow-2xl border border-white/50 p-3 bg-white/60 backdrop-blur-2xl">
            <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3">
              {/* Destination */}
              <Select value={selectedRoute} onValueChange={setSelectedRoute}>
                <SelectTrigger className="h-12 bg-white/70 border-white/60 rounded-xl text-gray-700 text-sm shadow-none focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Destination" />
                </SelectTrigger>
                <SelectContent className="bg-white text-black [&_[data-highlighted]]:bg-gray-100">
                  <SelectItem value="__all__">All Destinations</SelectItem>
                  {uniqueDropoffs.map((loc) => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Travel Date */}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="w-full flex items-center gap-2 bg-white/70 rounded-xl px-4 py-3 border border-white/60 text-left">
                    <CalendarIcon className="h-4 w-4 text-[#0B2421] shrink-0" />
                    <span className={`text-sm flex-1 truncate ${selectedDate ? "text-gray-700" : "text-gray-400"}`}>
                      {selectedDate ? format(selectedDate, "dd MMM yyyy") : "Travel date"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContentComp className="w-auto p-0" align="start">
                  <Calendar className="" classNames={{}} mode="single" selected={selectedDate} onSelect={setSelectedDate}
                    disabled={/** @param {Date} d */ (d) => d < new Date()} />
                </PopoverContentComp>
              </Popover>

              {/* Passengers */}
              <div className="h-12 flex items-center gap-2 bg-white/70 rounded-xl px-4 border border-white/60">
                <Users className="h-4 w-4 text-[#0B2421] shrink-0" />
                <input
                  type="number"
                  min={1}
                  max={8}
                  value={passengers}
                  onChange={(e) => setPassengers(Math.max(1, Math.min(8, parseInt(e.target.value, 10) || 1)))}
                  className="text-sm flex-1 bg-transparent outline-none text-gray-700 placeholder:text-gray-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <div className="flex flex-col shrink-0">
                  <button
                    type="button"
                    aria-label="Increase passengers"
                    onClick={() => setPassengers((n) => Math.min(8, n + 1))}
                    className="text-gray-400 hover:text-[#0B2421] leading-none transition-colors"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label="Decrease passengers"
                    onClick={() => setPassengers((n) => Math.max(1, n - 1))}
                    className="text-gray-400 hover:text-[#0B2421] leading-none transition-colors"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Search */}
              <button className="flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#C9A430] text-[#0B2421] font-bold rounded-xl px-8 py-3 transition-colors whitespace-nowrap">
                <Search className="h-4 w-4" />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Spacer + Cards */}
      <div className="pt-40 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-[#0B2421]">
              {filteredVehicles.length} {filteredVehicles.length === 1 ? "Option" : "Options"} Available
            </h2>
            <p className="text-gray-400 text-sm mt-1">
              {selectedRoute !== "__all__" ? `To ${selectedRoute}` : "Showing all available transportation"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVehicles.map((vehicle, index) => (
              <TransportCard key={vehicle.id} vehicle={vehicle} onSelect={() => onSelectVehicle(vehicle)} index={index} />
            ))}
          </div>

          {filteredVehicles.length === 0 && (
            <div className="text-center py-16">
              <p className="text-gray-400 text-lg">No vehicles found for the selected route.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
